import React, { useState, useEffect } from 'react';
import { checkConnection, connectDrive, disconnectDrive } from '../services/driveService';
import { toast } from 'sonner';
import { Loader2, HardDrive, Cloud } from 'lucide-react';

const PROVIDERS = [
  { id: 'google', name: 'Google Drive', IconComponent: HardDrive },
  { id: 'microsoft', name: 'OneDrive', IconComponent: Cloud },
];

function ProviderRow({ userId, provider, large = false }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const meta = PROVIDERS.find(p => p.id === provider);

  useEffect(() => {
    if (!userId) return;
    checkConnection(userId, provider)
      .then(setStatus)
      .catch(() => setStatus({ connected: false }))
      .finally(() => setLoading(false));
  }, [userId, provider]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await connectDrive(userId, provider);
      const updated = await checkConnection(userId, provider);
      setStatus(updated);
      toast.success(`${meta.name} connected!`);
    } catch (e) {
      toast.error(e.message || `Failed to connect ${meta.name}`);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setConnecting(true);
    try {
      await disconnectDrive(userId, provider);
      setStatus({ connected: false });
      toast.success(`${meta.name} disconnected`);
    } catch (e) {
      toast.error('Failed to disconnect');
    } finally {
      setConnecting(false);
    }
  };

  // Pulsing placeholder while connection status loads
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-1.5 px-2" aria-hidden="true">
        <div className="w-3.5 h-3.5 rounded bg-muted animate-pulse flex-shrink-0" />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="h-2.5 w-20 rounded bg-muted animate-pulse" />
          <div className="h-2 w-14 rounded bg-muted animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between rounded-lg transition-colors ${
      large ? 'py-2.5 px-3 border border-border/60 bg-background hover:bg-muted/40' : 'py-1.5 px-2 hover:bg-sidebar-accent/30'
    }`}>
      <div className={`flex items-center min-w-0 ${large ? 'gap-3' : 'gap-2'}`}>
        <meta.IconComponent className={`flex-shrink-0 text-primary ${large ? 'w-4 h-4' : 'w-3.5 h-3.5'}`} aria-hidden="true" />
        <div className="min-w-0">
          <p className={`font-medium truncate ${large ? 'text-sm text-foreground' : 'text-xs text-sidebar-foreground/80'}`}>{meta.name}</p>
          <p
            className={`text-muted-foreground truncate leading-tight ${large ? 'text-xs' : 'text-[10px]'}`}
            title={status?.connected ? (status.account_email || 'Connected') : 'Not connected'}
          >
            {status?.connected ? (status.account_email || 'Connected') : 'Not connected'}
          </p>
        </div>
      </div>
      <div className="flex-shrink-0 ml-2">
        {connecting ? (
          <Loader2 className={`animate-spin text-muted-foreground ${large ? 'w-4 h-4' : 'w-3 h-3'}`} aria-label="Working" />
        ) : status?.connected ? (
          <button
            onClick={handleDisconnect}
            aria-label={`Disconnect ${meta.name}`}
            className={`rounded border border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors ${
              large ? 'text-xs px-2.5 py-1' : 'text-[10px] px-1.5 py-0.5'
            }`}
          >
            Disconnect
          </button>
        ) : (
          <button
            onClick={handleConnect}
            aria-label={`Connect ${meta.name}`}
            className={`rounded bg-accent text-accent-foreground hover:bg-accent/90 transition-colors ${
              large ? 'text-xs px-2.5 py-1' : 'text-[10px] px-1.5 py-0.5'
            }`}
          >
            Connect
          </button>
        )}
      </div>
    </div>
  );
}

export default function DriveConnectionPanel({ userId, variant = 'sidebar' }) {
  if (!userId) return null;
  const large = variant === 'page';
  return (
    <div className={large ? 'space-y-2' : 'space-y-0.5'}>
      {!large && (
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-1">Cloud Storage</p>
      )}
      {PROVIDERS.map(p => (
        <ProviderRow key={p.id} userId={userId} provider={p.id} large={large} />
      ))}
    </div>
  );
}
